/**
 * Per-card descriptions for Tarot course (78 cards).
 * Finnish, symbolism- and psychology-focused. Source: Project Gutenberg pg43548 (adapted).
 * Keyed by card id: major = "the-fool", "the-magician", ...; minor = "wands-ace", "cups-02", etc.
 */

/** @type {Record<string, { imagery: string, meaning: string, shadow: string, readingThemes: string[], reflection: string }>} */
export const tarotCardDescriptions = {
  // —— Suuret arkanat (22) ——
  'the-fool': {
    imagery: 'Nuori kulkija seisoo kallion reunalla, katse horisontissa. Taskussa on säkki, takana koira. Hän ei vielä tiedä, mihin on menossa — ja juuri siksi kaikki on mahdollista.',
    meaning: 'Uusi alku, avoimuus, henki etsimässä kokemusta. Luottamus elämään ja omaan polkuun.',
    shadow: 'Vastuun välttely, naiivius, impulsiivisuus tai suunnan puute.',
    readingThemes: ['uusi suunta', 'hyppy tuntemattomaan', 'aloittaminen', 'luottamus'],
    reflection: 'Missä kohtaa elämässäni minua kutsutaan luottamaan, vaikka en vielä näe koko polkua?'
  },
  'the-magician': {
    imagery: 'Nuori hahmo pöydän äärellä: yllä elämän merkki (∞), vyöllä käärme, kädessä sauva. Pöydällä neljän elementin symbolit — kaikki työkalut ovat jo olemassa.',
    meaning: 'Toteutuminen, tahdon voima ja viisaus. Sinulla on kyvyt ja resurssit; aika käyttää niitä.',
    shadow: 'Manipulointi, huono suunnittelu tai hyödyntämättömät lahjat.',
    readingThemes: ['toiminta', 'luovuus', 'voima', 'aloite'],
    reflection: 'Mitä konkreettista asiaa voin tehdä tällä hetkellä käyttäen jo olemassa olevia voimavaroja?'
  },
  'the-high-priestess': {
    imagery: 'Istuu temppelin pylväiden välissä, jalkojen juuressa kuun sirppi. Kädessä käärö, jossa sana Tora. Verho takana — salattu tieto.',
    meaning: 'Intuitio, salattu perinne, sisäinen viisaus. Vastaukset ovat sisällä, ei ulkopuolella.',
    shadow: 'Salaisuudet, irtautuminen intuitiosta tai liiallinen vetäytyminen.',
    readingThemes: ['intuitio', 'salatut asiat', 'odotus', 'sisäinen ääni'],
    reflection: 'Mitä minun tulisi kuulla enemmän kuin ajatella — ja missä tilassa se on mahdollista?'
  },
  'the-empress': {
    imagery: 'Ylhäinen hahmo istumassa; diadeemissa tähdet, kilpessä Venuksen symboli. Vilja, vesi ja runsaus ympärillä.',
    meaning: 'Hedelmällisyys, hoiva, maallinen paratiisi. Luonto, keho ja luovuus kukoistavat.',
    shadow: 'Riippuvuus, tukahduttaminen tai kasvun puute.',
    readingThemes: ['runsaus', 'luovuus', 'keho', 'yhteys luontoon'],
    reflection: 'Mistä minun kannattaa huolehtia lempeämmin — itsestäni vai jostain ulkopuolelta?'
  },
  'the-emperor': {
    imagery: 'Kruunattu hallitsija istuu valtaistuimella; sauva (crux ansata), toisessa kädessä pallo. Istuimen käsinojissa pään symbolit.',
    meaning: 'Rakenne, auktoriteetti, toteutus. Maallinen ja älyllinen johtajuus.',
    shadow: 'Liiallinen kontrolli, jäykkyys tai dominointi.',
    readingThemes: ['raja', 'kurinalaisuus', 'vastuu', 'järjestys'],
    reflection: 'Missä elämäni tarvitsee selkeää rakenetta — ja missä joustavuutta?'
  },
  'the-hierophant': {
    imagery: 'Kolmoiskruunu, pylväiden välissä. Kädessä kolmoisristillä varustettu sauva, jalkojen juuressa ristikkäin avaimet. Kaksi pappia polvillaan.',
    meaning: 'Perinne, opetus, ulkoinen uskonto ja instituutio. Viisaus, jota jaetaan.',
    shadow: 'Sokea noudattaminen, dogma tai vapauden puute.',
    readingThemes: ['opettaja', 'perinne', 'yhteisö', 'rituaali'],
    reflection: 'Mistä perinteestä tai yhteisöstä haluan ottaa vastaan ohjausta — ja missä luottaa omaan polkuun?'
  },
  'the-lovers': {
    imagery: 'Aurinko yllä, siivekäs hahmo levittää käsivartensa. Edessä kaksi nuorta: mies Puun elämän, nainen Puun tietämyksen ja käärmeen luona.',
    meaning: 'Rakkaus, valinta ja arvojen linjaus. Ihmisrakkaus osana polkua.',
    shadow: 'Epäharmonia, arvojen epälinjaus tai itsensä sivuuttaminen.',
    readingThemes: ['valinta', 'rakkaus', 'arvojen yhtenäisyys', 'liitto'],
    reflection: 'Mikä valinta nyt vaatii, että kuuntelen oikeasti omaa sydäntäni?'
  },
  'the-chariot': {
    imagery: 'Voittaja seisoo vaunuissa, miekka kädessä. Olkapäillä Urim ja Thummim. Sfinksit vetävät vaunuja.',
    meaning: 'Voitto, tahdonvoima ja edistysaskel. Voit selättää esteet, kun pysyt keskittyneenä.',
    shadow: 'Kontrollin puute, suuntaamattomuus tai sisäinen vastarinta.',
    readingThemes: ['määrätietoisuus', 'esteet', 'liike eteenpäin', 'voitto'],
    reflection: 'Mitä minun täytyy pitää kurissa tai suunnassa, jotta eteneminen on mahdollista?'
  },
  'strength': {
    imagery: 'Nainen sulkee leijonan suun tai johtaa sitä kukkaisketjulla. Ei väkivaltaa — lempeä voima.',
    meaning: 'Sisäinen voima, rohkeus ja myötätunto. Voima, joka tulee sydämestä.',
    shadow: 'Epävarmuus, heikkous tai raaka tunne.',
    readingThemes: ['rohkeus', 'myötätunto', 'rauhoittaminen', 'luottamus'],
    reflection: 'Missä tilanteessa minun kannattaa vastata lempeydellä eikä voimalla?'
  },
  'the-hermit': {
    imagery: 'Erakko korkealla, kädessä lyhty, jossa loistaa tähti. Hän ei etsi — hän on saavuttanut ja näyttää tietä.',
    meaning: 'Sisäänpäin kääntyminen, saavutettu viisaus. "Missä minä olen, sinäkin voit olla."',
    shadow: 'Eristäytyminen, yksinäisyys tai sisäisen ohjauksen sivuuttaminen.',
    readingThemes: ['yksinäisyys', 'pohtiminen', 'ohjaus', 'tauko'],
    reflection: 'Mitä minun tulisi nähdä tai hyväksyä, kun pysähdyn ja sammutan melun?'
  },
  'wheel-of-fortune': {
    imagery: 'Pyörä liikkeessä; kulmissa Ezeekielin neljä eläintä. Sfinksi tasapainossa. Pyörässä ROTA ja Jumalan nimi.',
    meaning: 'Kohtalo, syklit ja käännekohdat. Elämä on liikkeessä — hyväksy muutos.',
    shadow: 'Huono onni, vastustus muutosta tai syklien rikkominen.',
    readingThemes: ['muutos', 'käännekohta', 'karma', 'sykli'],
    reflection: 'Mitä sykliä elämässäni on nyt päättymässä tai alkamassa — ja miten suhtaudun siihen?'
  },
  'justice': {
    imagery: 'Nainen istuu pylväiden välissä, vaakaa ja miekka kädessä. Oikeus ja tasapaino.',
    meaning: 'Oikeudenmukaisuus, syy ja seuraus, totuus. Se, mitä lähetät, palaa.',
    shadow: 'Epäoikeudenmukaisuus, vastuun pakoilu tai epärehellisyys.',
    readingThemes: ['totuus', 'vastuu', 'tasa-arvo', 'päätös'],
    reflection: 'Mihin päätökseen minun on rehellisesti kohdattava oma osuuteni?'
  },
  'the-hanged-man': {
    imagery: 'Hahmo roikkuu jalustalla, jalat ristissä (fylfot), pää alaspäin. Ilme ei ole kärsimys — vaan syvä sisäänkäynti.',
    meaning: 'Tauko, antautuminen ja uusi näkökulma. Elämä keskeytettynä — ei kuolemana.',
    shadow: 'Viivyttely, tarpeeton uhraus tai jumittaminen.',
    readingThemes: ['antautuminen', 'päästäminen irti', 'uusi näkökulma', 'odotus'],
    reflection: 'Mistä minun kannattaa luopua tai pysähtyä, jotta näkökulma voisi muuttua?'
  },
  'death': {
    imagery: 'Ratsastaja mustalla lipulla, jossa mystinen ruusu. Kuningas, lapsi ja neito kaatuvat; pappi odottaa. Horisontissa aurinko.',
    meaning: 'Loppu, muutos ja muodonmuutos. Vanha täytyy päästää irti, jotta uusi syntyy.',
    shadow: 'Vastustus muutosta, kykenemättömyys edetä tai rappeutuminen.',
    readingThemes: ['päätös', 'muutos', 'siirtymä', 'uudistuminen'],
    reflection: 'Mikä elämässäni on jo kuoleva tai päättyvä — ja miten voin antaa sille arvon ennen irtipäästämistä?'
  },
  'temperance': {
    imagery: 'Siivekäs enkeli kaataa nestettä maljasta toiseen. Yksi jalka maassa, toinen vedessä. Otsassa aurinko, rinnassa seitsenluku.',
    meaning: 'Tasapaino, maltillisuus ja sekoittaminen. Psyyke ja materia tasapainottuvat.',
    shadow: 'Epätasapaino, liiallisuus tai pitkän tähtäimen visio puuttuu.',
    readingThemes: ['tasapaino', 'kärsivällisyys', 'yhdistäminen', 'tarkoitus'],
    reflection: 'Missä elämäni tarvitsee vähemmän ääripäätä ja enemmän keskitietä?'
  },
  'the-devil': {
    imagery: 'Vuohihahmo alttarilla, kädessä käännetyt soihtu ja pentagrammi. Kaksi hahmoa ketjuissa — häntä, mutta ihmisäly kasvoissa.',
    meaning: 'Kiinnike, materia, varjo. Mikä sitoo — ja tunnistus, että vapautus on mahdollinen.',
    shadow: 'Riippuvuus, pimeä puoli tai sokea toistaminen.',
    readingThemes: ['riippuvuus', 'rajoitus', 'varjo', 'vapautuminen'],
    reflection: 'Mihin uskomukseen tai tapaan olen sitoutunut niin, että se tuntuu "pakolta"?'
  },
  'the-tower': {
    imagery: 'Salama iskee torniin, hahmoja putoaa. Vanha rakennelma murenee.',
    meaning: 'Äkillinen muutos, paljastus, vanhan romahdus. Perusta horjuu — uudelleenrakennus mahdollistuu.',
    shadow: 'Pelko muutoksesta, sisäinen räjähdys tai katastrofin välttely.',
    readingThemes: ['mullistus', 'herääminen', 'paljastus', 'uudelleenrakentaminen'],
    reflection: 'Mikä "torni" minussa tai elämässäni on jo murtumassa — ja miten voin hyväksyä sen?'
  },
  'the-star': {
    imagery: 'Alaston nainen polvillaan maassa, toinen jalka vedessä. Kaataa elämän vettä kahdesta kannusta. Yllä iso tähti ja seitsemän pienempää.',
    meaning: 'Toivo, totuus paljastettuna, uudistuminen. Valo pimeyden jälkeen.',
    shadow: 'Uskon puute, epätoivo tai irrallaan olo.',
    readingThemes: ['toivo', 'parantuminen', 'intuitio', 'tarkoitus'],
    reflection: 'Mihin pieneen toivoon voin antaa tilaa tällä hetkellä?'
  },
  'the-moon': {
    imagery: 'Kuu valaisee polun kahden tornin välissä. Koira ja susi ulvovat. Vesistä nousee rapuja — jotain kovaa pintaan.',
    meaning: 'Mielikuvitus, pelko, peilit. Asiat eivät aina ole sitä, miltä näyttävät.',
    shadow: 'Harha, tukahdutetut tunteet tai sisäinen sekavuus.',
    readingThemes: ['alitajunta', 'pelko', 'intuitio', 'epävarmuus'],
    reflection: 'Mitä pelkoa tai harhaa minun kannattaa kohdata lempeästi?'
  },
  'the-sun': {
    imagery: 'Lapsi valkoisella hevosella, punainen viiri kädessä. Aurinko loistaa; muuri takana, puutarha.',
    meaning: 'Valo, ilo, menestys ja elinvoima. Tietoisuus hengessä — yksinkertaisuus ja uudelleensyntyminen.',
    shadow: 'Alakulo, liian optimistinen tai sisäisen lapsen sivuuttaminen.',
    readingThemes: ['ilo', 'menestys', 'terveys', 'lapsen sydän'],
    reflection: 'Mistä voin tänään ottaa vastaan valoa ja iloa ilman syyllisyyttä?'
  },
  'judgement': {
    imagery: 'Enkeli puhaltaa trumpetilla; lipussa risti. Kuolleet nousevat haudoistaan — naisen, miehen ja lapsen muotoja.',
    meaning: 'Herääminen, uudistuminen, kutsu. Sisäinen kutsu vastataan.',
    shadow: 'Itsetietoisuuden puute, epäilys tai itsensä tuomitseminen.',
    readingThemes: ['arviointi', 'herääminen', 'anteeksianto', 'uusi vaihe'],
    reflection: 'Mihin ääneen sisälläni minun tulisi vastata — ja mitä antaa anteeksi?'
  },
  'the-world': {
    imagery: 'Nainen tanssii ovaalin seppeleen sisällä; neljä eläintä kulmissa. Täydellinen sykli.',
    meaning: 'Täyttyminen, saavutus, maailma kokonaisuutena. Sykli sulkeutuu.',
    shadow: 'Keskeneräisyys, sulkeutumisen puute tai kykenemättömyys täyttyä.',
    readingThemes: ['valmistuminen', 'matka', 'kokonaisuus', 'juhla'],
    reflection: 'Mitä sykliä olen sulkemassa — ja miten juhlin sitä?'
  },

  // —— Sauvat (Wands) ——
  'wands-ace': {
    imagery: 'Käsi pilvestä pitää sauvaa; luovuuden ja tahdon siemen.',
    meaning: 'Luovuus, aloite, uusi into. Tahdon voima ja mahdollisuus.',
    shadow: 'Lähtemättömyys, pelko aloittaa tai energia jumissa.',
    readingThemes: ['aloitus', 'into', 'luovuus', 'suunta'],
    reflection: 'Mikä "tulen" siemen minussa haluaa syttyä juuri nyt?'
  },
  'wands-02': {
    imagery: 'Hahmo katsoo merelle ja rantaan; toisessa kädessä pallo, toisessa sauva. Ruusu ja risti.',
    meaning: 'Valinta suunnan ja mahdollisuuden välillä. Suunnittelua ja vision laajentamista.',
    shadow: 'Epäröinti, suru maailman suuruudesta tai fyysinen kuormitus.',
    readingThemes: ['valinta', 'vision laajentaminen', 'suunnittelu'],
    reflection: 'Mitä kahta polkua näen edessäni — ja mitä tarvitsen hyväksyä valitakseni?'
  },
  'wands-03': {
    imagery: 'Rauhallinen hahmo kallion reunalla, kolme sauvaa maassa. Laivat merellä — kauppa ja yhteistyö.',
    meaning: 'Vakiintunut voima, yritys, kauppa, löytö. Yhteistyö ja laajentuminen.',
    shadow: 'Vaikeuksien päättyminen, tauko tai pettymyksen lopettaminen.',
    readingThemes: ['yhteistyö', 'laajentuminen', 'kauppa', 'löytö'],
    reflection: 'Missä voin rakentaa yhteistyötä tai odottaa tuloksia kärsivällisesti?'
  },
  'wands-04': {
    imagery: 'Neljä sauvaa, seppele, kaksi hahmoa kukkakimppujen kanssa. Silta ja kartano taustalla.',
    meaning: 'Koti, turvasatama, rauha, yhteisö ja täyttyminen.',
    shadow: 'Sama positiivisena — runsaus, kauneus, lisäys.',
    readingThemes: ['koti', 'rauha', 'yhteisö', 'juhla'],
    reflection: 'Mistä löydän turvaa ja rauhaa tällä hetkellä?'
  },
  'wands-05': {
    imagery: 'Nuoret heiluttavat sauvoja — kilpailu, väännös, elämän taistelu.',
    meaning: 'Kilpailu, kamppailu, pyrkimys voittoon. Voiman testaus.',
    shadow: 'Riita, erimielisyys, petos tai ristiriita.',
    readingThemes: ['kilpailu', 'haaste', 'taistelu', 'sinnikkyys'],
    reflection: 'Missä kohtaan hyväksyn kilpailun tai konfliktin osana kasvua?'
  },
  'wands-06': {
    imagery: 'Laulustella ratsastaja, seppeleellä koristettu sauva. Jalkaväki sauvoineen.',
    meaning: 'Voitto, hyvä uutinen, toive täyttyy. Kunniakulku.',
    shadow: 'Pelko vihollisen edessä, petos tai viivästys.',
    readingThemes: ['voitto', 'uutinen', 'kunnia', 'toive'],
    reflection: 'Mitä voittoa tai hyvää uutista voin jo nyt juhlia?'
  },
  'wands-07': {
    imagery: 'Nuori korkealla kalliolla, sauva pystyssä; kuusi muuta sauvaa nousee kohti.',
    meaning: 'Rohkeus, asema, puolustus. Kuusi vastaan yksi — silti etu.',
    shadow: 'Epäröinti, ahdistus tai varovaisuus.',
    readingThemes: ['rohkeus', 'puolustus', 'asema', 'kilpailu'],
    reflection: 'Missä minun kannattaa pitää asemani eikä luovuttaa?'
  },
  'wands-08': {
    imagery: 'Sauvat lentävät kohti maalia; liike, vauhti, lähestyminen.',
    meaning: 'Toiminta, nopeus, toive lähellä. Liike kohti tavoitetta.',
    shadow: 'Kateus, sisäinen riita, omatunto tai perhekonflikti.',
    readingThemes: ['nopeus', 'liike', 'toive', 'lähestyminen'],
    reflection: 'Mikä on nyt liikkeellä — ja mitä se vaatii minulta?'
  },
  'wands-09': {
    imagery: 'Hahmo nojaa sauvaan, odottaa. Takana kahdeksan sauvaa — aidan tai palisaadin muodostama.',
    meaning: 'Voima vastarintaa vastaan. Puolustus, viivästys, sinnikkyys.',
    shadow: 'Esteet, vastoinkäymiset tai katastrofi.',
    readingThemes: ['puolustus', 'sinnikkyys', 'odotus', 'raja'],
    reflection: 'Missä minun täytyy pitää pintaa — ja mistä voin hakea voimaa?'
  },
  'wands-10': {
    imagery: 'Hahmo kantaa kymmentä sauvaa — taakka, kuormitus.',
    meaning: 'Painostus, rasitus. Myös menestys voi olla raskas.',
    shadow: 'Vaikeudet, juonet, ristiriidat.',
    readingThemes: ['taakka', 'vastuu', 'rasitus', 'jakaminen'],
    reflection: 'Mitä taakkaa voin jakaa tai laskea alas?'
  },
  'wands-page': {
    imagery: 'Nuori seisoo, julistaa tai kuuntelee. Uutisia, viesti.',
    meaning: 'Uusi alku, viesti, uteliaisuus. Nuori henki tai uutinen.',
    shadow: 'Epävarmuus, huono uutinen tai levottomuus.',
    readingThemes: ['viesti', 'aloittaminen', 'uteliaisuus', 'mahdollisuus'],
    reflection: 'Mikä viesti tai kutsu odottaa vastaustani?'
  },
  'wands-knight': {
    imagery: 'Ratsastaja liikkeessä, lyhyt sauva kädessä. Pyramideja tai kumpuja taustalla.',
    meaning: 'Lähtö, muutto, muutos. Into, kiire, uusi suunta.',
    shadow: 'Rikko, jako, keskeytys tai erimielisyys.',
    readingThemes: ['liike', 'muutos', 'into', 'matka'],
    reflection: 'Mihin suuntaan minun on aika lähteä?'
  },
  'wands-queen': {
    imagery: 'Kuningatar istuu, sauva kädessä. Leijona tuolissa. Kukat, elämä.',
    meaning: 'Luottamus, rehellisyys, intohimo. Vahva naisenergia.',
    shadow: 'Kateus, petos, vastakkainasettelu tai epäluottamus.',
    readingThemes: ['luovuus', 'vetovoima', 'rohkeus', 'sisäinen mestaruus'],
    reflection: 'Miten voin ilmaista kuningattaren voimaani tässä tilanteessa?'
  },
  'wands-king': {
    imagery: 'Kuningas istuu, kukkimassa sauva kädessä. Leijona tuolissa.',
    meaning: 'Rehellisyys, maaseutu, ystävällisyys. Uutinen perinnöstä tai odotuksesta.',
    shadow: 'Ankaruus, siedätys — silti hyvä.',
    readingThemes: ['johtajuus', 'suunta', 'rehellisyys', 'vastuu'],
    reflection: 'Missä minun kannattaa johtaa esimerkillä eikä pelkästään sanoilla?'
  },

  // —— Maljat (Cups) ——
  'cups-ace': {
    imagery: 'Käsi pilvestä tarjoaa maljaa; kyyhkynen tuo hostian. Neljä virtaa, vesililjat.',
    meaning: 'Sydämen koti, ilo, rauha, ravitsemus. Pyhä pöytä.',
    shadow: 'Epävakaus, muutos, vallankumous tai vääryys.',
    readingThemes: ['rakkaus', 'alku', 'vastaanottavuus', 'ilo'],
    reflection: 'Mistä saan juuri nyt sydämen ravintoa?'
  },
  'cups-02': {
    imagery: 'Nuori pari maljoineen; caduceus ja leijonan pää yllä. Liitto, yhteys.',
    meaning: 'Rakkaus, ystävyys, yhteenkuuluvuus. Sukupuolten välinen yhteys.',
    shadow: 'Sama — intohimo, tasapaino.',
    readingThemes: ['kumppanuus', 'yhteys', 'sopimus', 'rakkaus'],
    reflection: 'Ken kanssa tai minkä asian kanssa haluan syventää yhteyttä?'
  },
  'cups-03': {
    imagery: 'Kolme neitoa maljoineen, juhla puutarhassa.',
    meaning: 'Täyttyminen, ilo, voitto, parantuminen.',
    shadow: 'Liika nautinto, aistillisuus tai päihde.',
    readingThemes: ['juhla', 'yhteisö', 'parantuminen', 'täyttyminen'],
    reflection: 'Mitä voin tänään juhlia yhdessä muiden kanssa?'
  },
  'cups-04': {
    imagery: 'Nuori istuu puun alla, kolme maljaa edessä. Pilvestä tarjolla neljäs — hän ei innostu.',
    meaning: 'Kyllästyminen, etääntyminen, unelmointi. Toinen malja tarjolla, mutta silmät kiinni.',
    shadow: 'Uutuus, enne, uudet suhteet.',
    readingThemes: ['tyytymättömyys', 'vetäytyminen', 'uusi tarjous'],
    reflection: 'Mistä olen kyllästynyt — ja mitä uutta en uskalla vielä vastaanottaa?'
  },
  'cups-05': {
    imagery: 'Hahmo mustassa viitassa, kolme maljaa maassa, kaksi pystyssä. Silta, pieni linna.',
    meaning: 'Menetys, suru — jotain jää. Perintö tai siirto, ei odotusten mukaista.',
    shadow: 'Uutinen, liitto, paluu tai väärät projektit.',
    readingThemes: ['menetys', 'suru', 'päästäminen irti', 'perintö'],
    reflection: 'Mitä menetystä tarvitsen kunnioittaa ennen kuin voin edetä?'
  },
  'cups-06': {
    imagery: 'Lapset vanhassa puutarhassa, maljat täynnä kukkia. Muisto, menneisyys.',
    meaning: 'Menneisyys, muistot, lapsuus. Ilon lähde usein menneessä.',
    shadow: 'Tulevaisuus, uudistuminen, jotain tulossa.',
    readingThemes: ['muisto', 'nostalgia', 'lapset', 'yhteisö'],
    reflection: 'Mikä muisto tai kokemus antaa minulle vielä tänään lämpöä?'
  },
  'cups-07': {
    imagery: 'Outoja maljoja, kuvia, unelmia. Mielikuvituksen maljat.',
    meaning: 'Unelmat, kuvat, mielikuvitus. Saavutus unelmien tasolla — ei vielä konkreettinen.',
    shadow: 'Tahto, päätös, projekti.',
    readingThemes: ['unelma', 'valinta', 'mielikuvitus', 'visionääri'],
    reflection: 'Mitä unelmaa kannattaa pitää elävänä — ja mitä laskea alas?'
  },
  'cups-08': {
    imagery: 'Hahmo kääntyy pois maljoistaan, lähtee. Jättää jotain taakse.',
    meaning: 'Poistuminen, luopuminen. Asian merkitys voi olla pieni.',
    shadow: 'Ilo, juhla, runsaus.',
    readingThemes: ['luopuminen', 'muutos', 'matka', 'päästäminen irti'],
    reflection: 'Mistä on aika kääntää selkä — lempeästi?'
  },
  'cups-09': {
    imagery: 'Hahmo tyytyväisenä, maljoja kaarella taustalla. Runsaus, turvallisuus.',
    meaning: 'Sopu, tyytyväisyys, voitto. Querentille hyvä olo.',
    shadow: 'Virhe, epätäydellisyys tai pettymys.',
    readingThemes: ['tyytyväisyys', 'runsaus', 'terveys', 'turvallisuus'],
    reflection: 'Mistä olen syvästi kiitollinen tänään?'
  },
  'cups-10': {
    imagery: 'Sateenkaari maljoineen; pari ja lapset alla. Koti, perhe, täydellinen onni.',
    meaning: 'Sydämen rauha, täydellinen rakkaus ja ystävyys. Koti, kylä, ympäristö.',
    shadow: 'Väärä sydän, viha tai väkivalta.',
    readingThemes: ['perhe', 'koti', 'täyttyminen', 'sopimus'],
    reflection: 'Ken kanssa tai missä ympäristössä tunten itseni kotoisaksi?'
  },
  'cups-page': {
    imagery: 'Nuori tuijottaa maljaa, josta kala nousee. Mielen kuvat muotoutuvat.',
    meaning: 'Uutinen, viesti, opiskelu. Nuori tai palvelija, yhteys querentiin.',
    shadow: 'Vietti, houkutus, petos tai viekoittelu.',
    readingThemes: ['uutinen', 'unelma', 'opiskelu', 'herkkä'],
    reflection: 'Mikä viesti sydämestäni odottaa kuulemista?'
  },
  'cups-knight': {
    imagery: 'Ratsastaja siivekkäällä kypärällä, rauhallinen. Unelma liikkeessä.',
    meaning: 'Saapuminen, tarjous, kutsu. Romantiikka tai diplomaattinen askel.',
    shadow: 'Petos, kavaluus, huijaus.',
    readingThemes: ['romantiikka', 'tarjous', 'matka', 'tunne'],
    reflection: 'Mikä kutsu tai tarjous vaatii vastaustani?'
  },
  'cups-queen': {
    imagery: 'Kuningatar meren äärellä, malja kädessä. Laiva, delfiini.',
    meaning: 'Hyvä nainen, rehellinen, palvelija. Rakkaus, intuitio, visio. Äiti, kumppani.',
    shadow: 'Epäluotettavuus, vika, petos tai turmelus.',
    readingThemes: ['empatia', 'hoiva', 'intuitio', 'vastaanottavuus'],
    reflection: 'Miten voin olla kuningattaren tavoin lempeä itselleni ja muille?'
  },
  'cups-king': {
    imagery: 'Kuningas istuu merellä, malja ja lyhyt sauva. Laiva, delfiini.',
    meaning: 'Rehellinen mies, laki, taide, tiede. Luova äly, tasapaino.',
    shadow: 'Epärehellisyys, kavaluus, vääryys tai menetys.',
    readingThemes: ['tasapaino', 'viisaus', 'emotionaalinen auktoriteetti', 'isä'],
    reflection: 'Missä minun kannattaa johtaa tunteillani tasapainoisesti?'
  },

  // —— Miekat (Swords) ——
  'swords-ace': {
    imagery: 'Käsi pilvestä pitää miekkaa; kruunu kärjen ympärillä.',
    meaning: 'Voitto, voima, valloitus. Suuri voima rakkaudessa ja vihassa.',
    shadow: 'Katastrofi, sama negatiivisena; toisinaan raskaus, lisäys.',
    readingThemes: ['selkeys', 'totuus', 'läpimurto', 'päätös'],
    reflection: 'Mikä yksi totuus minun on hyväksyä, jotta voin edetä?'
  },
  'swords-02': {
    imagery: 'Nainen silmät sidottuina, kaksi miekkaa olkapäillä. Tasapaino.',
    meaning: 'Tasapaino, ystävyys, sopimus. Rohkeus aseissa.',
    shadow: 'Petos, vale, kaksijakoisuus.',
    readingThemes: ['tasapaino', 'valinta', 'rehellisyys', 'päätös'],
    reflection: 'Mitä kahta asiaa yritän pitää tasapainossa — ja tarvitsenko?'
  },
  'swords-03': {
    imagery: 'Kolme miekkaa lävistävät sydämen; pilvi ja sade.',
    meaning: 'Erottaminen, poissaolo, viivästys, hajoaminen.',
    shadow: 'Mielenhäiriö, virhe, häiriö, sekaannus.',
    readingThemes: ['suru', 'ero', 'pettymys', 'haavoittuvuus'],
    reflection: 'Mitä surua tai erottelua tarvitsen kunnioittaa?'
  },
  'swords-04': {
    imagery: 'Ritari rukouksessa haudalla, miekka viereen.',
    meaning: 'Valppaus, vetäytyminen, erakko, hautaus.',
    shadow: 'Hallitus, talous, varovaisuus, testamentti.',
    readingThemes: ['tauko', 'vetäytyminen', 'lepo', 'uudelleenarviointi'],
    reflection: 'Missä minun kannattaa pysähtyä ja olla tekemättä?'
  },
  'swords-05': {
    imagery: 'Voittaja katsoo kahta perääntyvää; miekkoja maassa ja olalla.',
    meaning: 'Alennus, tuhoutuminen, häpeä, menetys.',
    shadow: 'Sama; hautaus, muistotilaisuus.',
    readingThemes: ['konflikti', 'voitto', 'häviö', 'nöyryys'],
    reflection: 'Mistä voin päästää irti "voittamisen" tarpeesta?'
  },
  'swords-06': {
    imagery: 'Lautturi vie matkustajia toiselle rannalle. Tyyntä, kevyt lasti.',
    meaning: 'Matka vedellä, reitti, lähettiläs. Keino, tie ulos.',
    shadow: 'Paljastus, tunnustus, julkisuus; toisinaan rakkauskutsu.',
    readingThemes: ['siirtymä', 'matka', 'muutos', 'helpotus'],
    reflection: 'Mikä "matka" tai siirtymä on nyt tarpeen?'
  },
  'swords-07': {
    imagery: 'Hahmo kantaa viisi miekkaa; kaksi jää leiriin. Suunnitelma, yritys.',
    meaning: 'Suunnitelma, yritys, toivo. Riita, epäonnistunut suunnitelma.',
    shadow: 'Neuvo, opastus, panettelu.',
    readingThemes: ['strategia', 'varovaisuus', 'petos', 'toivo'],
    reflection: 'Mikä suunnitelma kannattaa pitää — ja mikä hylätä?'
  },
  'swords-08': {
    imagery: 'Nainen sidottu, silmät sidottu, miekkat ympärillä.',
    meaning: 'Huono uutinen, kriisi, rajoitus, konflikti. Sairaus.',
    shadow: 'Levottomuus, onnettomuus, petos, fatality.',
    readingThemes: ['rajoitus', 'pelko', 'kriisi', 'päästäminen irti'],
    reflection: 'Mistä rajoituksesta voin vapautua — kun uskallan?'
  },
  'swords-09': {
    imagery: 'Nainen sängyllä, suru, miekkat yllä. Syvä suru.',
    meaning: 'Kuolema, epäonnistuminen, viivästys, petos, epätoivo.',
    shadow: 'Vankeus, epäilys, häpeä.',
    readingThemes: ['suru', 'ahdistus', 'epätoivo', 'yksinäisyys'],
    reflection: 'Mitä surua tarvitsen antaa tilaa — ilman tuomitsemista?'
  },
  'swords-10': {
    imagery: 'Hahmo maassa, kymmenen miekkaa selässä.',
    meaning: 'Loppu, kärsimys, epätoivo. Ei välttämättä väkivaltainen kuolema.',
    shadow: 'Etu, voitto, suosio — ei pysyvä.',
    readingThemes: ['loppu', 'päätös', 'päästäminen irti', 'uusi alku'],
    reflection: 'Mikä jakso on nyt päättynyt — ja miten voin antaa sille arvon?'
  },
  'swords-page': {
    imagery: 'Nuori kävelee, miekka pystyssä. Valpas, varmistava.',
    meaning: 'Valvonta, vakoilu, palvelu. Uteliaisuus, uudet ideat.',
    shadow: 'Huono puoli; sairaus, valmistelemattomuus.',
    readingThemes: ['uteliaisuus', 'viesti', 'varovaisuus', 'uusi ajatus'],
    reflection: 'Mikä uusi ajatus tai viesti tarvitsee huomioni?'
  },
  'swords-knight': {
    imagery: 'Ratsastaja täydessä vauhdissa, miekka koholla. Sankari.',
    meaning: 'Taito, rohkeus, konflikti, tuho. Nopea toiminta.',
    shadow: 'Ylimielisyys, kykenemättömyys, tuhlaavuus.',
    readingThemes: ['toiminta', 'konflikti', 'rohkeus', 'muutos'],
    reflection: 'Missä minun täytyy toimia rohkeasti — vaikka pelottaa?'
  },
  'swords-queen': {
    imagery: 'Kuningatar miekka pystyssä, toinen käsi ojennettu. Tuttu surun kanssa.',
    meaning: 'Leskeys, naisen suru, poissaolo, kuivuus. Ei armoa.',
    shadow: 'Pahuus, suvaitsemattomuus, petos.',
    readingThemes: ['selkeys', 'raja', 'oikeus', 'itsensä suojelu'],
    reflection: 'Missä minun kannattaa olla terävä mutta ei kylmä?'
  },
  'swords-king': {
    imagery: 'Kuningas istuu tuomioistuimessa, paljastettu miekka.',
    meaning: 'Tuomio, valta, laki, äly. Kruunun virat.',
    shadow: 'Julmuus, väärä käyttö, pahuus.',
    readingThemes: ['auktoriteetti', 'totuus', 'päätös', 'raja'],
    reflection: 'Mihin päätökseen olen valmis — rehellisesti?'
  },

  // —— Lantit / Pentaklit (Pentacles) ——
  'pentacles-ace': {
    imagery: 'Käsi pilvestä pitää pentakkelia. Hedelmällisyys, mahdollisuus.',
    meaning: 'Täydellinen tyytyväisyys, onni, äly. Kulta.',
    shadow: 'Rikkauden varjopuoli; toisinaan suuri rikkaus.',
    readingThemes: ['alku', 'mahdollisuus', 'materia', 'siemen'],
    reflection: 'Mikä konkreettinen mahdollisuus on nyt käsillä?'
  },
  'pentacles-02': {
    imagery: 'Nuori tanssii, kaksi pentakkelia kädessä, yhdistettynä kahdeksikon muotoon.',
    meaning: 'Ilo, vapaa-aika. Toisinaan uutinen, kirjoitus, esteet.',
    shadow: 'Pakotettu ilo, kirjoitus, vaihto.',
    readingThemes: ['tasapaino', 'liike', 'raha', 'vaihto'],
    reflection: 'Miten voin tasapainottaa materiaa ja iloa?'
  },
  'pentacles-03': {
    imagery: 'Kuvanveistäjä luostarissa, ammattitaito. Palkinto, mestarillisuus.',
    meaning: 'Ammatti, kauppa, taito. Yleensä kunnia, arvostus, maine.',
    shadow: 'Keskinkertaisuus, heikkous.',
    readingThemes: ['taituruus', 'oppiminen', 'yhteistyö', 'tulos'],
    reflection: 'Mistä taidosta tai työstä voin olla ylpeä?'
  },
  'pentacles-04': {
    imagery: 'Kruunattu hahmo pitää pentakkelia; kaksi jalkojen alla.',
    meaning: 'Turva, omaisuus, perintö. Pitää kiinni.',
    shadow: 'Viivästys, vastustus.',
    readingThemes: ['turvallisuus', 'säästäminen', 'koti', 'perintö'],
    reflection: 'Mistä pidän kiinni turvallisuuden vuoksi — ja onko se terveellistä?'
  },
  'pentacles-05': {
    imagery: 'Kaksi kerjäläistä lumimyrskyssä, valo ikkunassa.',
    meaning: 'Materiaalinen puute, köyhyys. Toisinaan rakkaus, ystävyys, sopusointu.',
    shadow: 'Sekaannus, kaaos, turmelus.',
    readingThemes: ['puute', 'nöyryys', 'jakaminen', 'toivo'],
    reflection: 'Mitä puutetta voin kohdata avoimesti — ja kenen kanssa jakaa?'
  },
  'pentacles-06': {
    imagery: 'Kauppias punnitsee rahaa, jakaa köyhille.',
    meaning: 'Lahjat, kiitollisuus. Toisinaan huomio, vauraus.',
    shadow: 'Kateus, himo, harha.',
    readingThemes: ['jakaminen', 'antaminen', 'oikeudenmukaisuus', 'yhteisö'],
    reflection: 'Mitä voin antaa — aikaa, rahaa, taitoa — ilman että puuttuu itseltäni?'
  },
  'pentacles-07': {
    imagery: 'Nuori nojaa sauvaan, tuijottaa seitsemää pentakkelia kasvustossa.',
    meaning: 'Raha, kauppa, vaihto. Toisinaan riita; toisinaan viattomuus.',
    shadow: 'Huoli lainasta tai rahasta.',
    readingThemes: ['sijoittaminen', 'kärsivällisyys', 'tulos', 'arviointi'],
    reflection: 'Mihin "sijoituksen" tulokset näkyvät vasta myöhemmin?'
  },
  'pentacles-08': {
    imagery: 'Käsityöläinen työssä, pentakkeleja ja trofeeita.',
    meaning: 'Työ, ammattitaito, kauppa. Valmisteleva vaihe.',
    shadow: 'Turha kunnianhimo, ahneus; toisinaan oveluus.',
    readingThemes: ['työ', 'taituruus', 'harjoittelu', 'edistyminen'],
    reflection: 'Mikä taito tai projekti vaatii vielä kärsivällisyyttä?'
  },
  'pentacles-09': {
    imagery: 'Nainen puutarhassa, lintu ranteella. Viiniköynnökset, runsaus.',
    meaning: 'Varovaisuus, turvallisuus, menestys, taito. Luotettavuus.',
    shadow: 'Petos, huijaus, pettäminen.',
    readingThemes: ['runsaus', 'turvallisuus', 'luonto', 'terveys'],
    reflection: 'Mistä löydän materiaalista ja emotionaalista turvallisuutta?'
  },
  'pentacles-10': {
    imagery: 'Perhe kaaren alla, portti taloon. Lapset, koirat, vanha hahmo.',
    meaning: 'Rikkaus, perhe, perintö, koti.',
    shadow: 'Sattuma, fatality, menetys; toisinaan lahja, myötäjä.',
    readingThemes: ['perhe', 'koti', 'pitkäjänteisyys', 'perintö'],
    reflection: 'Mikä "koti" tai perintö haluan rakentaa tai kunnioittaa?'
  },
  'pentacles-page': {
    imagery: 'Nuori tuijottaa pentakkelia kohotettujen käsiensä yllä. Hitaasti liikkeellä.',
    meaning: 'Opiskelu, harjoittelu, pohdinta. Uutinen, viesti, hallinta.',
    shadow: 'Tuhlaavuus, hajautuminen, epäedullinen uutinen.',
    readingThemes: ['oppiminen', 'uusi taito', 'mahdollisuus', 'uteliaisuus'],
    reflection: 'Mitä uutta taitoa tai mahdollisuutta olen vastaanottamassa?'
  },
  'pentacles-knight': {
    imagery: 'Ratsastaja raskas hevonen, pentakkel kädessä. Hidas, kestävä.',
    meaning: 'Käytännöllisyys, palvelus, vastuu. Normaali, ulkoinen taso.',
    shadow: 'Hitaus, laiskuus, lammeys.',
    readingThemes: ['vastuu', 'edistyminen', 'kunnianhimo', 'käytännöllisyys'],
    reflection: 'Missä minun kannattaa edetä hitaasti ja varmasti?'
  },
  'pentacles-queen': {
    imagery: 'Kuningatar tumma, vakava; tuijottaa pentakkeliaan. Suuri sielu.',
    meaning: 'Runsaus, anteliaisuus, turvallisuus, vapaus.',
    shadow: 'Epäilys, pelko, epäluottamus.',
    readingThemes: ['hoiva', 'luonto', 'turvallisuus', 'anteliaisuus'],
    reflection: 'Miten voin olla kuningattaren tavoin antelias itselleni ja ympäristölle?'
  },
  'pentacles-king': {
    imagery: 'Kuningas istuu, härän pää tuolissa. Pentakkelit, materia.',
    meaning: 'Rohkeus, äly, liike, matemaattiset lahjat. Menestys.',
    shadow: 'Heikkous, turmelus, vaara.',
    readingThemes: ['johtajuus', 'raha', 'auktoriteetti', 'pitkäjänteisyys'],
    reflection: 'Missä minun kannattaa johtaa käytännöllisesti ja kestävästi?'
  }
};

// Helper: get description id for a course card (major: name-based, minor: suit-rankKey)
export function getCardDescriptionId(card) {
  if (card.isMinor && card.suit && card.rankKey) {
    return `${card.suit}-${card.rankKey}`;
  }
  if (card.name) {
    return card.name.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and');
  }
  return null;
}

export function getCardDescription(card) {
  const id = getCardDescriptionId(card);
  return id ? tarotCardDescriptions[id] : null;
}
